// "Satan mockup" gostergesi + atama.
//
// Ilham listesine (analiz) atanan satan mockup, o listeden uretilen listenin
// ANA GORSELI olur. Atanmissa gorseliyle gosterilir; atanmamissa buradan
// secilebilir. readOnly ile sadece gosterim (tasarim kartlari gibi yerlerde).

import { useEffect, useState } from "react";
import { Star, Loader2, X, ChevronDown } from "lucide-react";
import { MockupChooserModal } from "./MockupChooserModal";
import { authHeaders, getBackendUrl } from "../../lib/http";

export interface SellingMockupInfo {
  id: string;
  label: string;
  imageUrl: string;
  thumbnailUrl?: string | null;
  productTypeKey?: string | null;
  /** Katalogtan atanan degerler — aramada da kullanilir */
  brand?: string | null;
  model?: string | null;
  color?: string | null;
  /** Tasarim alani tanimli mi (alan yoksa tasarim yerlestirilemez) */
  designAreaW?: number;
  designAreaH?: number;
  designAreas?: unknown[];
  isSelling?: boolean;
}


/** "Atama kaldirildi" isareti — null'dan ayirt etmek icin (null = yerel secim yok) */
const NONE = { __none: true } as const;

const abs = (u?: string | null) =>
  !u ? "" : u.startsWith("http") || u.startsWith("data:") ? u : `${getBackendUrl()}${u}`;

export function SellingMockupPicker({
  analysisId,
  designId,
  inspiration,
  value,
  productTypeKey,
  readOnly,
  compact,
  onChange,
}: {
  /** Atama yapilacak analiz — readOnly ise gerekmez */
  analysisId?: string;
  /** Verilirse atama TASARIMA yapilir (analiz yerine). Ayni ilham listesinden
   *  cikan tasarimlarin her birinin satan mockup'i farkli olabilir. */
  designId?: string;
  /** Karsilastirma: ilham listesinin fotografi (modalin saginda buyuk) */
  inspiration?: { imageUrl: string; title?: string | null } | null;
  value: SellingMockupInfo | null;
  /** Secim listesini bu kategoriye gore daralt */
  productTypeKey?: string | null;
  readOnly?: boolean;
  compact?: boolean;
  onChange?: (m: SellingMockupInfo | null) => void;
}) {
  const [open, setOpen] = useState(false);
  // Atama sonrasi ust bilesen tazelenene kadar ekran "Atanmamis" gosteriyordu;
  // secim yerelde de tutulur.
  const [localValue, setLocalValue] = useState<SellingMockupInfo | typeof NONE | null>(null);
  // Modalda ileri/geri gezerken bilesen yeniden kurulmuyor: onceki tasarimin
  // secimi SONRAKINE tasinip "hepsine uygulandi" gibi gorunuyordu.
  useEffect(() => { setLocalValue(null); }, [designId, analysisId, value?.id]);
  const [saving, setSaving] = useState(false);

  async function assign(m: SellingMockupInfo | null) {
    setLocalValue(m ?? NONE);
    setOpen(false);
    if (!designId && !analysisId) { onChange?.(m); return; }
    setSaving(true);
    try {
      // Tasarim verildiyse TASARIMA ata; yoksa (eski davranis) ilham listesine
      const res = designId
        ? await fetch(`${getBackendUrl()}/api/designs/${designId}/selling-mockup`, {
            method: "PATCH",
            headers: authHeaders(),
            body: JSON.stringify({ mockupId: m?.id ?? null }),
          })
        : await fetch(`${getBackendUrl()}/api/analyses/${analysisId}/production`, {
            method: "PATCH",
            headers: authHeaders(),
            body: JSON.stringify({ sellingMockupId: m?.id ?? null }),
          });
      if (res.ok) onChange?.(m);
    } catch {
      /* ag hatasi — kullanici tekrar deneyebilir */
    } finally {
      setSaving(false);
    }
  }

  // Gosterimde once YEREL secim (atama yeni yapildiysa), sonra prop
  const shownValue: SellingMockupInfo | null =
    localValue === NONE ? null : (localValue as SellingMockupInfo | null) ?? value;

  // ── Sadece gosterim ──
  if (readOnly) {
    if (!shownValue) return null;
    return (
      <span
        title={`Satan mockup: ${shownValue.label}`}
        className="inline-flex items-center gap-1 rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700"
      >
        <img src={abs(shownValue.thumbnailUrl || shownValue.imageUrl)} alt="" className="h-4 w-4 rounded object-cover" />
        <Star className="h-2.5 w-2.5 fill-current" />
        {!compact && <span className="max-w-[90px] truncate">{shownValue.label}</span>}
      </span>
    );
  }

  // ── Gosterim + atama ──
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={saving}
        title="Bu ilham listesinden üretilen listenin ana görseli"
        className={`flex w-full items-center gap-2 rounded-lg border px-2 py-1.5 text-left text-xs transition-colors ${
          shownValue ? "border-amber-300 bg-amber-50/60" : "border-dashed border-gray-300 bg-white hover:border-amber-400"
        }`}
      >
        {shownValue ? (
          <img src={abs(shownValue.thumbnailUrl || shownValue.imageUrl)} alt="" className="h-8 w-8 shrink-0 rounded object-cover" />
        ) : (
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded border border-dashed border-gray-300">
            <Star className="h-3.5 w-3.5 text-gray-300" />
          </span>
        )}
        <span className="min-w-0 flex-1">
          <span className="block text-[10px] font-medium uppercase tracking-wide text-amber-700">Satan mockup</span>
          <span className="block truncate text-gray-700">{shownValue ? shownValue.label : "Atanmamış — seç"}</span>
        </span>
        {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" /> : <ChevronDown className="h-3.5 w-3.5 text-gray-400" />}
      </button>

      {open && (
        <MockupChooserModal
          title="Satan mockup seç"
          mode="single"
          productTypeKey={productTypeKey}
          selectedIds={shownValue ? [shownValue.id] : []}
          compare={shownValue ? { label: shownValue.label, imageUrl: shownValue.thumbnailUrl || shownValue.imageUrl } : null}
          inspiration={inspiration ?? null}
          onPick={(m) => void assign(m as SellingMockupInfo)}
          onClose={() => setOpen(false)}
        />
      )}

      {/* Atamayi kaldir — modal disinda, hizli erisim */}
      {shownValue && (
        <button
          type="button"
          onClick={() => void assign(null)}
          className="mt-1 flex items-center gap-1 text-[10px] text-gray-400 hover:text-red-500"
        >
          <X className="h-2.5 w-2.5" /> Atamayı kaldır
        </button>
      )}
    </div>
  );
}
